import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Flame, Check, Sparkles, Undo2 } from "@/lib/icons";
import { TierInput } from "@/components/rubric/RubricParts";
import { needsValue, isNumeric, ruleText } from "@/lib/rubric";

function WeekDots({ count, goal, id }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={`keystone-week-dots-${id}`}>
      {Array.from({ length: goal }).map((_, i) => (
        <span key={`dot-${i + 1}`} className={`w-3 h-3 rounded-full transition-colors ${i < count ? "bg-amber-400" : "bg-white/15"}`} />
      ))}
    </div>
  );
}

function Stat({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400">{label}</div>
      <div className="mt-1 flex items-center gap-2">{children}</div>
    </div>
  );
}

export function KeystoneSpotlight({ habits, onCheckIn, onUndo, onFocus }) {
  const h = habits.find((x) => x.focus === "keystone");
  const [value, setValue] = useState("");
  if (!h) return <KeystonePrompt habits={habits} onFocus={onFocus} />;
  const weekDone = h.week_count >= h.goal;
  const measured = needsValue(h.rubric);
  const ready = !measured || value !== "";
  const todayLabel = h.today_tier && measured ? h.rubric.tiers.find((t) => t.key === h.today_tier)?.label : null;
  return (
    <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      className="relative overflow-hidden rounded-3xl bg-stone-900 text-stone-100 p-6 sm:p-8" data-testid="keystone-spotlight">
      <Star className="absolute -right-6 -top-6 w-40 h-40 text-amber-400/10 fill-amber-400/10 pointer-events-none" />
      <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">
            <Star className="w-3.5 h-3.5 fill-amber-300" /> Keystone habit
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2 truncate" data-testid="keystone-title">{h.title}{h.rooted && <span title="Rooted — this habit climbed its whole ladder" data-testid={`habit-rooted-${h.id}`} className="ml-2 inline-block align-middle w-3 h-6 bg-rose-700 [clip-path:polygon(0_0,100%_0,100%_100%,50%_75%,0_100%)]" />}</h2>
          {h.description && <p className="text-sm text-stone-400 mt-1 max-w-lg">{h.description}</p>}
          {measured && <div className="mt-2 flex flex-wrap gap-1.5" data-testid="keystone-rubric">{h.rubric.tiers.map((t) => <span key={t.key} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-stone-300"><span className="font-semibold text-stone-100">{t.label}</span> · {ruleText(h.rubric.type, t.rule, h.rubric.unit)}</span>)}</div>}
          <div className="flex flex-wrap gap-8 mt-5">
            <Stat label="Momentum" testid="keystone-streak">
              <Flame className={`w-5 h-5 ${h.streak > 0 ? "text-orange-400 fill-orange-500/40" : "text-stone-600"}`} />
              <span className="font-mono text-2xl font-bold text-amber-300">{h.streak}</span>
              <span className="text-xs text-stone-400">day{h.streak === 1 ? "" : "s"} in a row</span>
            </Stat>
            <Stat label={`This week · ${(h.goal_label || "").toLowerCase()}`} testid="keystone-week">
              <span className="font-mono text-2xl font-bold text-amber-300">{h.week_count}<span className="text-stone-500">/{h.goal}</span></span>
              <WeekDots count={h.week_count} goal={h.goal} id={h.id} />
              {weekDone && <span className="text-xs font-semibold text-emerald-300 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> goal met</span>}
            </Stat>
            {h.tier_mix?.total > 0 && measured && (
              <Stat label={`Full-rate · ${h.tier_mix.days} days`} testid="keystone-full-rate">
                <span className="font-mono text-2xl font-bold text-amber-300">{Math.round((h.tier_mix.full_rate || 0) * 100)}%</span>
              </Stat>
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-stretch lg:items-end gap-2">
          {measured && !h.completed_today && <TierInput rubric={h.rubric} value={value} onChange={setValue} dark idPrefix="keystone" />}
          {!h.completed_today && (h.rubric.type === "self" || (h.stage && h.rubric.tiers.length > 1)) ? (
            <div className="flex flex-col items-stretch lg:items-end gap-1.5" data-testid="keystone-tier-buttons">
              {h.stage && <span className="text-[10px] font-bold uppercase tracking-widest text-amber-200/80">Stage {h.stage.index + 1} · {h.stage.current}</span>}
              <div className="flex flex-wrap gap-1.5 justify-end">
                {h.rubric.tiers.map((t, i) => <button key={t.key} data-testid={`keystone-tier-${t.key}`} onClick={() => onCheckIn(h, undefined, t.key)} title={t.rule?.text || ""}
                  className={`text-sm font-bold px-4 py-2 rounded-full transition active:scale-95 ${i === 0 ? "bg-amber-400 text-stone-900 hover:bg-amber-300" : "bg-white/10 text-amber-100 hover:bg-white/20"}`}>{t.label}</button>)}
              </div>
            </div>
          ) : (
          <button data-testid="keystone-checkin-btn" onClick={() => onCheckIn(h, measured ? (isNumeric(h.rubric.type) ? Number(value) : value) : undefined)} disabled={h.completed_today || !ready}
            className={`w-full lg:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-bold transition-transform active:scale-95 ${
              h.completed_today ? "bg-emerald-500/20 text-emerald-200 cursor-default" : "bg-amber-400 text-stone-900 hover:bg-amber-300 shadow-lg shadow-amber-400/20 disabled:opacity-40"}`}>
            <Check className="w-5 h-5" /> {h.completed_today ? (todayLabel ? `Done · ${todayLabel}` : "Done for today") : measured ? "Log today" : "Done today"}
          </button>
          )}
          {h.completed_today ? (
            <button data-testid="keystone-undo-btn" onClick={() => onUndo(h)} className="mt-2 w-full lg:w-auto inline-flex items-center justify-center gap-1 text-[11px] text-stone-400 hover:text-amber-200 font-semibold">
              <Undo2 className="w-3 h-3" /> Undo — marked by mistake
            </button>
          ) : (
            <div className="text-[11px] text-stone-500 mt-2 text-center lg:text-right">Keystone check-ins earn +5 extra</div>
          )}
        </div>
      </div>
    </motion.section>
  );
}

function KeystonePrompt({ habits, onFocus }) {
  if (habits.length === 0) return null;
  return (
    <div className="rounded-3xl border-2 border-dashed border-amber-300/80 bg-amber-50/60 p-5 sm:p-6" data-testid="keystone-prompt">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-2xl bg-amber-400 flex items-center justify-center shrink-0"><Star className="w-5 h-5 text-stone-900 fill-stone-900" /></div>
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-display text-lg font-bold text-stone-900">Pick a Keystone habit</h3>
          <p className="text-sm text-stone-600 mt-1">One habit gets the spotlight up here — bigger rewards, full attention. Which one matters most right now?</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {habits.map((h) => (
              <button key={h.id} data-testid={`keystone-pick-${h.id}`} onClick={() => onFocus(h, "keystone")}
                className="text-sm px-3.5 py-1.5 rounded-full bg-white border border-amber-200 text-stone-800 font-semibold hover:bg-amber-400 hover:border-amber-400 transition-colors">
                {h.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
